import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles(theme => ({
  linkItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    gap: 12,
    textDecoration: 'none',
    color: 'inherit',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  description: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  externalIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    opacity: 0.7,
    flexShrink: 0,
  },
}));

type QuickLinkProps = {
  icon: React.ReactNode;
  label: string;
  description?: string;
  href: string;
  iconBgColor?: string;
  iconColor?: string;
  external?: boolean;
  onClick?: () => void;
};

export const QuickLink = ({
  icon,
  label,
  description,
  href,
  iconBgColor = 'rgba(82, 146, 244, 0.09)',
  iconColor = '#5292F4',
  external = false,
  onClick,
}: QuickLinkProps) => {
  const classes = useStyles();

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <a
      href={href}
      className={classes.linkItem}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
    >
      <Box
        className={classes.iconContainer}
        style={{ backgroundColor: iconBgColor }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          style: { fontSize: 16, color: iconColor },
        })}
      </Box>
      <Box className={classes.content}>
        <Typography className={classes.label}>{label}</Typography>
        {description && (
          <Typography className={classes.description}>{description}</Typography>
        )}
      </Box>
      {external && <OpenInNewIcon className={classes.externalIcon} />}
    </a>
  );
};
